import to from "await-to-js";
import { GetComponentProps } from "rc-table/lib/interface";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/Button/Button";
import { ActionsCell } from "../../../../components/CellRenderers/ActionsCell/ActionsCell";
import { Table } from "../../../../components/Table/Table";
import { SettingsContext } from "../../../../modules/settings/settings.context";
import {
  CompanyShort,
  ICompanyObject,
} from "../../../../modules/settings/settings.types";

import { ColumnsType } from "antd/es/table";
import { ModalsContext } from "../../../../context/modals.context";
import { settingsService } from "../../../../modules/settings/settings.service";
import { CompanyObjectModal } from "../CompanyObjectModal/CompanyObjectModal";
import style from "./CompanyObjects.module.scss";

import { toastService } from "../../../../utils/toast.service";

export const CompanyObjects = () => {
  const {
    settings: { companyObjects, selectedObject, objectTypes },
    setSettings,
  } = useContext(SettingsContext);
  const { handleUpdateModals, clearModals, setError } =
    useContext(ModalsContext);
  const { t } = useTranslation();

  const objectTypesOptions = objectTypes.map((oType) => ({
    ...oType,
    value: oType.id,
    label: oType.naziv,
  }));

  const getCompanyObjects = async () => {
    const [err, res] = await to(settingsService.getCompanyObjects());
    if (err || !res.success) return setError(res?.errorMessage);
    setSettings({
      companyObjects: res.data.objekti,
      objectTypes: res.data.objekatTipovi,
    });
  };

  const getObjectDetails = async (objectId?: number) => {
    if (!objectId) return;
    const [err, res] = await to(settingsService.getObjectDetails(objectId));
    if (err || !res.success) return setError(res?.errorMessage);
    setSettings({
      selectedObject: res.data,
    });
    handleUpdateModals({ modalVisible: "company_object" });
  };

  const createCompanyObject = async (cObject: ICompanyObject) => {
    const [err, res] = await to(settingsService.addCompanyObject(cObject));
    if (err || !res.success) return setError(res?.errorMessage);
    toastService.success("TOAST.CREATE_OBJECT_SUCCESS");
    clearModals();
    getCompanyObjects();
  };

  const editCompanyObject = async (cObject: ICompanyObject) => {
    const [err, res] = await to(settingsService.editCompanyObject(cObject));
    if (err || !res.success) return setError(res?.errorMessage);
    clearModals();
    toastService.success("TOAST.EDIT_OBJECT_SUCCESS");
    getCompanyObjects();
  };

  const clearObject = () => {
    setSettings({ selectedObject: null });
  };

  const handleCreate = () => {
    handleUpdateModals({ modalVisible: "company_object" });
  };

  const onRowHandler: GetComponentProps<CompanyShort> = (row) => ({
    onClick: () => handleEdit(row),
  });

  const handleEdit = (companyObject: CompanyShort) => {
    getObjectDetails(companyObject.id);
  };

  const columns: ColumnsType<CompanyShort> = [
    { dataIndex: "id", title: t("TABLES.ID"), width: 100 },
    { dataIndex: "naziv", title: t("SETTINGS_OBJECTS.NAZIV") },
    {
      dataIndex: "ulicaIBroj",
      title: t("SETTINGS_OBJECTS.ULICA_BROJ"),
    },
    {
      dataIndex: "gradIPostanskiBroj",
      title: t("SETTINGS_OBJECTS.GRAD_POSTANSKI_BROJ"),
    },
    { dataIndex: "odgovornoLice", title: t("SETTINGS_OBJECTS.ODGOVORNO_LICE") },
    { dataIndex: "oznakaVozila", title: t("SETTINGS_OBJECTS.OZNAKA_VOZILA") },

    {
      title: t("COMMON.AKCIJE"),
      render: (companyObject: CompanyShort) => (
        <ActionsCell handleEdit={() => handleEdit(companyObject)} />
      ),
    },
  ];

  useEffect(() => {
    getCompanyObjects();
  }, []);

  return (
    <div className={style.container}>
      <CompanyObjectModal
        selectedObject={selectedObject}
        createCompanyObject={createCompanyObject}
        editCompanyObject={editCompanyObject}
        clearObject={clearObject}
        objectTypes={objectTypesOptions}
        refetchObjects={getCompanyObjects}
      />
      <div className={style.actions} id="object_actions">
        <Button name="add-object" type="primary" onClick={handleCreate}>
          {t("SETTINGS_OBJECTS.NOVI_OBJEKAT")}
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        scrollConfig={[".ant-tabs-nav", "#object_actions"]}
        dataSource={companyObjects}
        rowClassName="pointer"
        onRow={onRowHandler}
        name="company-objects"
      ></Table>
    </div>
  );
};
